import {
  provide,
  inject,
  ref,
  watchPostEffect,
  defineComponent,
  watch,
  computed,
  onUnmounted,
  unref,
  onMounted,
  watchEffect
} from 'vue';
import type { InjectionKey, Ref, PropType } from 'vue';
import useSuspend from '../lib/useSuspend';
import evaluateCondition from '../lib/evaluateCondition';

const lockablesInjectionKey = Symbol('lockables') as InjectionKey<
  Ref<Lockable[]>
>;

const injectLockables = () => inject(lockablesInjectionKey, ref([]));

/**
 * Manages lockable state for a component that may contain completable activities.
 *
 * Provides a method to add completable IDs to the lockable, along with the methods of lockables higher up the tree.
 *
 * @param id
 */
export function useLockables(
  id: string,
  { condition }: { condition?: MaybeRef<Condition> } = {}
): LockableStatus {
  const suspend = useSuspend();
  const injected = injectLockables();
  const locked = ref(true);

  // the store where completables add themselves to this lockable
  const completables = ref(new Set<string>());

  // we'll use them as an array
  const completablesList = computed(() => [...completables.value.values()]);

  // for debugging in devtools
  const completedCompletables = computed(() =>
    completablesList.value.filter(id => suspend?.completed[id])
  );

  // each lockable provides methods for completables to register themselves
  const lockable = computed<Lockable>(() => ({
    id,
    add: (c: string) => completables.value.add(c),
    remove: (c: string) => completables.value.delete(c)
  }));

  const provided = computed(() => [...injected.value, lockable.value]);

  provide(lockablesInjectionKey, provided);

  // block legacy completion of closest secton
  provide('sectionId', id);

  //  "post" ensures completables are added before computing locked status
  watchPostEffect(() => {
    const c = unref(condition);

    if (c) {
      // default "of" to all completables
      const missingOf = typeof c === 'object' && 'of' in c && !c.of;
      if (missingOf) c.of = completablesList.value;

      // evaluate the condition
      locked.value = !evaluateCondition(c, suspend.completed);
    } else {
      const haveCompletables = completablesList.value.length > 0;

      // if there are completables, complete them all
      locked.value = haveCompletables
        ? completablesList.value.some(id => !suspend.completed[id])
        : false;
    }
  });

  return { id, locked, completables, completedCompletables };
}

type MaybeRef<T> = T | Ref<T>;
type IdsParam = MaybeRef<string | string[] | Set<string> | undefined>;

const normalizeIds = (ids?: IdsParam): Set<string> => {
  const _ids = unref(ids);
  if (!_ids) return new Set();
  if (_ids instanceof Set) return _ids;
  const arr = Array.isArray(_ids) ? _ids : [_ids];
  return new Set(arr);
};

/**
 * A composition function that adds completable IDs to provided lockables.
 *
 * Accepts a list of IDs that are completable by the component. The list may be one string, an array of strings, or a Set of strings. The IDs are added to all provided lockables.
 *
 * If the list is a ref, then lockables are updated when the list changes.
 *
 * When the component is unmounted, the IDs are removed from all provided lockables.
 *
 * @param ids
 */
export function useCompletables(ids: IdsParam) {
  const idsRef = computed(() => normalizeIds(ids));
  const lockables = injectLockables();

  const updateLockables = (ids: IdsParam, prevIds?: IdsParam) => {
    const setIds = normalizeIds(ids);
    const setOldIds = normalizeIds(prevIds);

    lockables.value.forEach(({ add, remove }) => {
      setOldIds.forEach(id => {
        if (!setIds.has(id)) remove(id);
      });

      setIds.forEach(id => {
        if (!setOldIds.has(id)) add(id);
      });
    });
  };

  onMounted(() => updateLockables(idsRef));
  onUnmounted(() => updateLockables([], idsRef));

  watch(idsRef, (curr, prev) => updateLockables(curr, prev), {
    immediate: true,
    flush: 'post'
  });
}

export const UseLockables = defineComponent({
  name: 'UseLockables',

  props: {
    id: { type: String, required: true },
    condition: { type: Object as PropType<Condition>, default: undefined },
    statusRef: {
      type: Function as PropType<(status: LockableStatus) => void>,
      default: undefined
    }
  },

  setup: props => {
    const { id } = props;
    const condition = computed(() => props.condition);
    const status = useLockables(id, { condition });
    if (props.statusRef) props.statusRef(status);

    return status;
  },

  render() {
    const status: LockableStatus = {
      id: this.id,
      locked: this.locked,
      completables: this.completables,
      completedCompletables: this.completedCompletables
    };

    return this.$slots.default?.(status);
  }
});

export const UseCompletables = defineComponent({
  name: 'UseCompletables',
  props: { ids: { type: Array as PropType<IdsParam>, default: [] } },
  setup: props => useCompletables(props.ids),

  render() {
    return this.$slots.default?.();
  }
});
